import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import { ArrayField, FormGroup, NumberStepper, Select } from '.';

type ReminderInterval = {
  unit: 'days' | 'weeks' | 'months';
  value: number;
};

interface Props {
  disabled?: boolean;
  field: string;
  type: 'completion' | 'deadline';
}

const ReminderIntervalsSelector = ({
  disabled = false,
  field,
  type
}: Props) => {
  const { messages } = useReduxState();

  return (
    <FormGroup
      label={messages.commonReminderIntervals}
      labelHelpText={
        type === 'deadline'
          ? messages.commonReminderIntervalsDeadlineInfo
          : messages.commonReminderIntervalsCompletionInfo
      }
    >
      <ArrayField field={field}>
        {({ addWithInitialValue }) => (
          <div className="reminders">
            <ArrayField.Items<{ value: number }>>
              {({
                getState,
                field: itemField,
                remove,
                values,
                initialValue
              }) => {
                const currentValue =
                  (values as ReminderInterval)?.value ||
                  (initialValue as ReminderInterval).value;

                const isOnlyValue =
                  (getState()?.values?.[field] as any[])?.length === 1;

                return (
                  <div className="reminder-item">
                    <NumberStepper
                      disabled={disabled}
                      field={`${itemField}.value`}
                      min={1}
                      stepValue={1}
                      inputStyle={{ width: 100 }}
                    />

                    <Select
                      disabled={disabled}
                      dropdownStyles={{ fontSize: '1rem' }}
                      field={`${itemField}.unit`}
                      options={
                        currentValue > 1
                          ? [
                              { label: messages.commonDays, value: 'days' },
                              { label: messages.commonWeeks, value: 'weeks' },
                              { label: messages.commonMonths, value: 'months' }
                            ]
                          : [
                              { label: messages.commonDay, value: 'days' },
                              { label: messages.commonWeek, value: 'weeks' },
                              { label: messages.commonMonth, value: 'months' }
                            ]
                      }
                      style={{ width: 120 }}
                    />

                    <Button
                      disabled={disabled || isOnlyValue}
                      category="light"
                      icon="trash-alt"
                      onClick={remove}
                      size="small"
                      tooltip={messages.commonRemove}
                    />
                  </div>
                );
              }}
            </ArrayField.Items>

            <Button
              category="light"
              disabled={disabled}
              icon="plus"
              onClick={() => addWithInitialValue({ unit: 'days', value: 1 })}
              text={messages.commonReminderIntervalsAdd}
              style={{ width: 'max-content', marginTop: 10 }}
            />
          </div>
        )}
      </ArrayField>

      <style jsx>{`
        .reminders {
          counter-reset: ordinals;
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 5px;
        }
        .reminder-item {
          align-items: center;
          display: flex;
          gap: 10px;
          height: 35px;
        }
        .reminder-item::before {
          counter-increment: ordinals;
          content: counter(ordinals) '.';
          font-size: 1.6rem;
        }
      `}</style>
    </FormGroup>
  );
};

export default ReminderIntervalsSelector;
